import { RootState } from '@AppState/redux-store'
import { CustomerServiceAPI } from '@API/data-services/customer-service'
import { CustomerConfigResponse, PlayerDataCustomerConfig } from '@Generated/back-office-client/src'
import {
  Box,
  Button,
  Card,
  Container,
  Flex,
  Heading,
  Link,
  Switch,
  Text,
  TextArea,
  TextField,
} from '@radix-ui/themes'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

type BoEditableConfig = {
  freshdeskId?: string | null
  claimFormInactivityTimeoutMs?: number | null
}

export const CustomerConfig = () => {
  const { activeCustomerId } = useSelector((state: RootState) => state.users)
  const queryClient = useQueryClient()

  const [boConfig, setBoConfig] = useState<BoEditableConfig>({})
  const [pdConfig, setPdConfig] = useState<PlayerDataCustomerConfig>({})

  const boQueryKey = ['customer-config', activeCustomerId]
  const pdQueryKey = ['player-data-config', activeCustomerId]

  const {
    data: boData,
    isLoading: boLoading,
    isError: boIsError,
    error: boFetchError,
  } = useQuery({
    queryKey: boQueryKey,
    queryFn: () => CustomerServiceAPI.getCustomerConfig(),
    enabled: !!activeCustomerId,
  })

  const {
    data: pdData,
    isLoading: pdLoading,
    isError: pdIsError,
    error: pdFetchError,
  } = useQuery({
    queryKey: pdQueryKey,
    queryFn: () => CustomerServiceAPI.getPlayerDataConfig(activeCustomerId!),
    enabled: !!activeCustomerId,
  })

  // Initialize the editable BO draft state when query data lands.
  useEffect(() => {
    if (boData) {
      setBoConfig({
        freshdeskId: boData.freshdeskId ?? null,
        claimFormInactivityTimeoutMs: boData.claimFormInactivityTimeoutMs ?? null,
      })
    }
  }, [boData])

  // Initialize the editable PD draft state when query data lands.
  useEffect(() => {
    if (pdData) {
      setPdConfig(pdData)
    }
  }, [pdData])

  const {
    mutate: saveBoConfig,
    isPending: boSaving,
    isError: boSaveIsError,
    error: boSaveError,
  } = useMutation({
    mutationFn: () => {
      if (!activeCustomerId) {
        return Promise.reject(new Error('No active customer'))
      }
      return CustomerServiceAPI.patchCustomerConfig(activeCustomerId, {
        freshdeskId: boConfig.freshdeskId ?? undefined,
        claimFormInactivityTimeoutMs: boConfig.claimFormInactivityTimeoutMs ?? undefined,
      })
    },
    onSuccess: (updated) => {
      queryClient.setQueryData<CustomerConfigResponse>(boQueryKey, (existing) => ({
        ...(existing ?? { timezone: '' }),
        ...updated,
      }))
    },
  })

  const {
    mutate: savePdConfig,
    isPending: pdSaving,
    isError: pdSaveIsError,
    error: pdSaveError,
  } = useMutation({
    mutationFn: () => {
      if (!activeCustomerId) {
        return Promise.reject(new Error('No active customer'))
      }
      return CustomerServiceAPI.patchPlayerDataConfig(activeCustomerId, {
        claimFormEnabled: pdConfig.claimFormEnabled ?? false,
        allowDeviceTicketScans: pdConfig.allowDeviceTicketScans ?? false,
        ticketWalletEnabled: pdConfig.ticketWalletEnabled ?? false,
        emailSender: pdConfig.emailSender,
        appHostName: pdConfig.appHostName,
        claimsReservationHostUrl: pdConfig.claimsReservationHostUrl,
        firebaseServiceAccountJson: pdConfig.firebaseServiceAccountJson,
        clerk: pdConfig.clerk,
      })
    },
    onSuccess: (updated: PlayerDataCustomerConfig) => {
      queryClient.setQueryData(pdQueryKey, updated)
    },
  })

  const errorMessage = (fetchError: unknown, saveError: unknown): string | null => {
    if (fetchError) {
      return fetchError instanceof Error ? fetchError.message : 'Failed to load'
    }
    if (saveError) {
      return saveError instanceof Error ? saveError.message : 'Failed to save'
    }
    return null
  }

  const boErrorMessage = errorMessage(boIsError && boFetchError, boSaveIsError && boSaveError)
  const pdErrorMessage = errorMessage(pdIsError && pdFetchError, pdSaveIsError && pdSaveError)

  return (
    <Container>
      <Flex direction="column" gap="4">
        <Heading>Customer Config</Heading>

        <Card>
          <Flex direction="column" gap="3">
            <Heading size="4">Back Office Settings</Heading>

            {boLoading ? (
              <Text>Loading...</Text>
            ) : (
              <>
                <Box>
                  <Text as="label" size="2" weight="medium">
                    Freshdesk ID
                  </Text>
                  <TextField.Root
                    value={boConfig.freshdeskId ?? ''}
                    onChange={(e) =>
                      setBoConfig({
                        ...boConfig,
                        freshdeskId: e.target.value === '' ? null : e.target.value,
                      })
                    }
                  />
                </Box>

                <Box>
                  <Text as="label" size="2" weight="medium">
                    Claim form inactivity timeout (ms)
                  </Text>
                  <TextField.Root
                    type="number"
                    value={
                      boConfig.claimFormInactivityTimeoutMs == null
                        ? ''
                        : String(boConfig.claimFormInactivityTimeoutMs)
                    }
                    onChange={(e) =>
                      setBoConfig({
                        ...boConfig,
                        claimFormInactivityTimeoutMs:
                          e.target.value === '' ? null : Number(e.target.value),
                      })
                    }
                  />
                </Box>

                {boErrorMessage && (
                  <Text color="red" size="2">
                    {boErrorMessage}
                  </Text>
                )}

                <Flex>
                  <Button onClick={() => saveBoConfig()} disabled={boSaving} loading={boSaving}>
                    Save
                  </Button>
                </Flex>
              </>
            )}
          </Flex>
        </Card>

        <Card>
          <Flex direction="column" gap="3">
            <Heading size="4">Player Data Settings</Heading>

            {pdLoading ? (
              <Text>Loading...</Text>
            ) : (
              <>
                <Flex align="center" gap="2">
                  <Switch
                    checked={pdConfig.claimFormEnabled ?? false}
                    onCheckedChange={(checked) =>
                      setPdConfig({ ...pdConfig, claimFormEnabled: checked })
                    }
                  />
                  <Text as="label" size="2" weight="medium">
                    Claim form enabled
                  </Text>
                </Flex>

                <Flex align="center" gap="2">
                  <Switch
                    checked={pdConfig.allowDeviceTicketScans ?? false}
                    onCheckedChange={(checked) =>
                      setPdConfig({ ...pdConfig, allowDeviceTicketScans: checked })
                    }
                  />
                  <Text as="label" size="2" weight="medium">
                    Allow device-only ticket scans
                  </Text>
                </Flex>

                <Flex align="center" gap="2">
                  <Switch
                    checked={pdConfig.ticketWalletEnabled ?? false}
                    onCheckedChange={(checked) =>
                      setPdConfig({ ...pdConfig, ticketWalletEnabled: checked })
                    }
                  />
                  <Text as="label" size="2" weight="medium">
                    Ticket Wallet enabled
                  </Text>
                </Flex>

                <Box>
                  <Text as="label" size="2" weight="medium">
                    Email sender
                  </Text>
                  <TextField.Root
                    value={pdConfig.emailSender ?? ''}
                    onChange={(e) =>
                      setPdConfig({
                        ...pdConfig,
                        emailSender: e.target.value === '' ? null : e.target.value,
                      })
                    }
                  />
                </Box>

                <Box>
                  <Text as="label" size="2" weight="medium">
                    App host name
                  </Text>
                  <TextField.Root
                    value={pdConfig.appHostName ?? ''}
                    onChange={(e) =>
                      setPdConfig({
                        ...pdConfig,
                        appHostName: e.target.value === '' ? null : e.target.value,
                      })
                    }
                  />
                </Box>

                <Box>
                  <Text as="label" size="2" weight="medium">
                    Claims reservation host URL
                  </Text>
                  <TextField.Root
                    value={pdConfig.claimsReservationHostUrl ?? ''}
                    onChange={(e) =>
                      setPdConfig({
                        ...pdConfig,
                        claimsReservationHostUrl: e.target.value === '' ? null : e.target.value,
                      })
                    }
                  />
                </Box>

                <Box>
                  <Text as="label" size="2" weight="medium">
                    Firebase service account JSON
                  </Text>
                  <Text as="p" size="1" color="gray" mb="1">
                    Paste the contents of the JSON file from Firebase Console → Project Settings →
                    Service Accounts → Generate new private key. See the{' '}
                    <Link
                      href="https://firebase.google.com/docs/admin/setup#initialize-sdk"
                      target="_blank"
                      rel="noopener noreferrer">
                      Firebase Admin SDK setup docs
                    </Link>
                    .
                  </Text>
                  <TextArea
                    rows={8}
                    value={pdConfig.firebaseServiceAccountJson ?? ''}
                    onChange={(e) =>
                      setPdConfig({
                        ...pdConfig,
                        firebaseServiceAccountJson: e.target.value === '' ? null : e.target.value,
                      })
                    }
                  />
                </Box>

                <Heading size="3">Clerk</Heading>

                <Box>
                  <Text as="label" size="2" weight="medium">
                    Publishable key
                  </Text>
                  <TextField.Root
                    value={pdConfig.clerk?.publishableKey ?? ''}
                    onChange={(e) =>
                      setPdConfig({
                        ...pdConfig,
                        clerk: {
                          ...pdConfig.clerk,
                          publishableKey: e.target.value === '' ? null : e.target.value,
                        },
                      })
                    }
                  />
                </Box>

                <Box>
                  <Text as="label" size="2" weight="medium">
                    Secret key
                  </Text>
                  <TextField.Root
                    value={pdConfig.clerk?.secretKey ?? ''}
                    onChange={(e) =>
                      setPdConfig({
                        ...pdConfig,
                        clerk: {
                          ...pdConfig.clerk,
                          secretKey: e.target.value === '' ? null : e.target.value,
                        },
                      })
                    }
                  />
                </Box>

                <Box>
                  <Text as="label" size="2" weight="medium">
                    Webhook secret
                  </Text>
                  <TextField.Root
                    value={pdConfig.clerk?.webhookSecret ?? ''}
                    onChange={(e) =>
                      setPdConfig({
                        ...pdConfig,
                        clerk: {
                          ...pdConfig.clerk,
                          webhookSecret: e.target.value === '' ? null : e.target.value,
                        },
                      })
                    }
                  />
                </Box>

                {pdErrorMessage && (
                  <Text color="red" size="2">
                    {pdErrorMessage}
                  </Text>
                )}

                <Flex>
                  <Button onClick={() => savePdConfig()} disabled={pdSaving} loading={pdSaving}>
                    Save
                  </Button>
                </Flex>
              </>
            )}
          </Flex>
        </Card>
      </Flex>
    </Container>
  )
}
